import type { MemberRolesMap, Role, Timestamp, UserId } from './common.js';

/**
 * Entry stored under `/projects/{pid}.members[userId]`. Denormalized for UI
 * rendering; authoritative role lookup is `memberRoles[userId]` on
 * sub-collection docs (see design-addendum.md §2).
 */
export interface ProjectMember {
  role: Role;
  displayName: string;
  email: string;
  /** Auth photoURL captured when invited; refreshed on user profile update. */
  photoURL?: string | null;
  /** Timestamp the member was added to the project. */
  addedAt: Timestamp;
}

/**
 * Per-project Backlog workspace settings. The API key itself is NEVER stored
 * in Firestore — only a reference to the Secret Manager secret name.
 */
export interface BacklogConfig {
  /** Full Backlog space URL, e.g. "https://sunup.backlog.com". */
  spaceUrl: string;
  /**
   * Name of the Secret Manager secret holding the API key.
   * Format: `backlog-api-key-{projectId}` (Cloud Function resolves at runtime).
   */
  apiKeySecretName: string;
  /**
   * Project keys in Backlog (e.g. ["MIG", "APG"]). One MCT project may
   * track issues from multiple Backlog projects under the same space —
   * sync iterates this list, and the webhook resolver matches incoming
   * payloads via `array-contains`.
   */
  backlogProjectKeys: string[];
  /**
   * Snapshot of Backlog project display names, keyed by `projectKey`,
   * captured at "保存" time from the Backlog API project list. Pure
   * presentation data — labels in chips / dropdowns / filters render as
   * `{name} ({key})` when an entry exists here, and fall back to the
   * bare key otherwise. Keys without entries (or stale entries pointing
   * at renamed Backlog projects) never block functionality; UI just
   * shows the key until the next save refreshes the snapshot.
   */
  backlogProjectNames?: Record<string, string>;
  /**
   * Last successful full-sync start time. `null` when never synced.
   * The sync Cloud Function uses this to decide between a full and diff sync.
   */
  lastSyncedAt: Timestamp | null;
}

/**
 * `/projects/{projectId}` - root project document.
 *
 * `memberUserIds` is the single source of truth for Security Rules on the
 * project document itself; per-role checks use `members[uid].role` on the
 * project doc, but sub-collection docs carry their own denormalized
 * `memberUserIds` + `memberRoles` to avoid `get()` in rules (see
 * design-addendum.md §2).
 */
export interface ProjectDoc {
  projectId: string;
  name: string;
  description: string;
  ownerUserId: UserId;
  /** Kept in sync with `Object.keys(members)` for `array-contains` queries. */
  memberUserIds: UserId[];
  members: Record<UserId, ProjectMember>;
  /**
   * Precomputed `{ [uid]: role }` for cheap denormalization onto
   * sub-collection docs. Mirrors `members[uid].role`.
   */
  memberRoles: MemberRolesMap;
  backlog: BacklogConfig | null;
  /**
   * User-managed option lists for WBS columns that render as selects
   * (領域 / ベンダー). Absent on legacy projects; UI falls back to
   * values derived from existing wbsItems.
   */
  wbsCatalog?: WbsCatalog;
  /**
   * Per-project schedule preferences. Absent on legacy projects; the
   * scheduled Cloud Functions fall back to sensible defaults when these
   * fields are missing (notifications: enabled+8時, backlogSync: enabled).
   */
  notifications?: NotificationSettings;
  backlogSync?: BacklogSyncSettings;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Persisted entry inside any `WbsCatalog.*` list (領域, ベンダー).
 *
 *   - `id` is stable across renames so we can diff edits and migrate
 *     wbsItem references on rename.
 *   - `label` is what the user sees and what `wbsItems[*]` stores.
 *   - `colorIndex`, when present, OVERRIDES the deterministic name-hash
 *     fallback. It's what gets written when the user explicitly picks a
 *     color in the editor or when a freshly-added entry gets the next
 *     unused slot. Index space is shared with `AssigneeDoc.colorIndex`
 *     (`ASSIGNEE_COLOR_SLOTS` modulus) so all chip surfaces (assignee /
 *     area / vendor) draw from the same palette.
 *
 * After the Phase 2 canonicalization this is the only stored shape —
 * the legacy `string[]` form has been migrated away by
 * `migrate:area-dropLegacy`.
 */
export interface WbsCatalogOption {
  id: string;
  label: string;
  colorIndex?: number;
}

export interface WbsCatalog {
  /** Display-ordered list of 領域 options. */
  area: ReadonlyArray<WbsCatalogOption>;
  /** Display-ordered list of ベンダー options. */
  vendor: ReadonlyArray<WbsCatalogOption>;
}

/**
 * Pass-through normalizer for catalog lists. Kept as a function so
 * callers in hot render paths can still depend on a stable identity
 * shape (`{ id, label, colorIndex? }`) even if storage layout shifts in
 * the future. Pure — safe in `useMemo` deps.
 *
 * Throws when handed a legacy `string[]` payload — this means a doc
 * still hasn't been migrated and the operator should run
 * `pnpm -F @mct/functions migrate:area-dropLegacy --apply`.
 */
export function normalizeCatalogOptions(
  stored: ReadonlyArray<WbsCatalogOption> | null | undefined,
): WbsCatalogOption[] {
  if (!stored) return [];
  return stored.map((entry) => {
    if (typeof entry === 'string') {
      throw new Error(
        'WbsCatalog still contains legacy string[] entries — run `pnpm -F @mct/functions migrate:area-dropLegacy --apply` to convert them to WbsCatalogOption[].',
      );
    }
    return {
      id: entry.id ?? entry.label,
      label: entry.label,
      ...(typeof entry.colorIndex === 'number'
        ? { colorIndex: entry.colorIndex }
        : {}),
    };
  });
}

/** Allowed values for `notifications.dailyHour` (JST, 24h). */
export const NOTIFICATION_DAILY_HOURS = [7, 8, 9, 10, 12, 18] as const;
export type NotificationDailyHour = (typeof NOTIFICATION_DAILY_HOURS)[number];

export interface NotificationSettings {
  /** Master switch for the daily-summary email. */
  enabled: boolean;
  /** JST hour (24h) at which to send the daily summary. */
  dailyHour: NotificationDailyHour;
  /**
   * When `true` (the default), completing a migration-work task
   * (`execution.status` → `'done'`) emails the assignees of every
   * directly-dependent successor task that they can now start.
   *
   * Gated under the same `enabled` master switch as the daily summary —
   * if `enabled === false`, no mail of any kind goes out regardless of
   * this flag. Absent on legacy docs; treated as `true` (opt-out, not
   * opt-in) so existing projects start getting these notifications once
   * deployed.
   */
  notifyOnTaskComplete?: boolean;
}

export interface BacklogSyncSettings {
  /**
   * Master switch for Backlog sync (issue cache + activity ingest).
   * `project.backlog === null` already gates these, so this flag is for
   * users who want to PAUSE sync without losing their config.
   */
  enabled: boolean;
}

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  enabled: true,
  dailyHour: 8,
  notifyOnTaskComplete: true,
};

export const DEFAULT_BACKLOG_SYNC_SETTINGS: BacklogSyncSettings = {
  enabled: true,
};

/**
 * Canonical label for a Backlog project. `{name} ({key})` when a name
 * snapshot exists, otherwise the bare key. Used everywhere we render a
 * Backlog project (settings chips, milestone-link dropdown, suggestion
 * inbox filter / chip / issue row, WBS / TaskList filters) so the
 * display stays consistent — and so the formatter is the single place
 * to evolve later (e.g. swapping to localized name lookups).
 */
export function formatBacklogProjectLabel(
  key: string,
  name: string | null | undefined,
): string {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed ? `${trimmed} (${key})` : key;
}
